import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { DropdownButton, ButtonGroup, Dropdown } from 'react-bootstrap';
import { List } from 'immutable';
import { injectIntl } from 'react-intl';
import Button from '../ui/Button';
import appConfig from '../../appConfig';
import { updateSortBy } from '../../actions/umoGallery';
import {
  showUMOGridView,
  showUMOGridNoJView,
  showUMOListView,
  showUMOSmGridNoJView,
} from '../../actions/mainUI';
import { viewTypes } from '../../constants/umoGallery';
import { SearchParams } from '../../utils/searchUtils';
import { getLabelFromAppConfig } from '../../utils/uiUtils';
import { validateSessionConnection } from '../../actions/session';
import { addError } from '../../actions/messageDialog';
import OptionsMenu from '../OptionsMenu';

const mapStateToProps = state => ({
  session: state.session,
  categories: state.categories,
});

const mapDispatchToProps = dispatch => ({
  sortByUpdate: (eventKey, isDesc) => dispatch(updateSortBy(eventKey, isDesc)),
  validateSession: surl => dispatch(validateSessionConnection(surl)),
  addError: err => dispatch(addError(err)),
});

function UMOToolbar({
  sortBy,
  sortByIsDescending,
  dispatch,
  sortByUpdate,
  umoGalleryViewType,
  searchParams,
  selectedWorkspaceData = { id: null },
  selectedUMOIDs,
  isSelectAll,
  isUMOGalleryWorkspace,
  copyUMOs,
  intl,
  umoList,
  session,
  toggleCompareView,
}) {
  // created as a general function to be able to clear modes when switching between views
  // in this case, sort mode
  const changeViewType = (viewType) => {
    switch (viewType) {
      case (viewTypes.LIST):
        dispatch(showUMOListView());
        break;
      case (viewTypes.GRID_NOJ):
        dispatch(showUMOGridNoJView());
        break;
      case (viewTypes.GRID):
        dispatch(showUMOGridView());
        break;
      case (viewTypes.SM_GRID_NOJ):
        dispatch(showUMOSmGridNoJView());
        break;
      default:
        break;
    }
  };
  const sortByItems = appConfig.umoGallery.sortByOptions.map((option, i) =>
    <Dropdown.Item
      className="umo-toolbar__sort-item"
      // the negation for isDescending allows it to work when sortByIsDescending is either
      // false or undefined
      active={option.value === sortBy && !option.isDescending === !sortByIsDescending}
      eventKey={option.value}
      key={i}
      data-isdesc={option.isDescending}
    >
      {
        option.isDescending ? <i className="umo-toolbar__sort-item--down" /> :
        option.isAscending && <i className="umo-toolbar__sort-item--up" />
      }
      &nbsp;{getLabelFromAppConfig(option, intl.locale)}
    </Dropdown.Item>
  );

  return (
    <div className="umo-gallery-header__umo-toolbar">
      <div className="umo-toolbar__sort-by">
        <ButtonGroup size="sm">
          <Button
            active={umoGalleryViewType === viewTypes.GRID}
            onClick={() => changeViewType(viewTypes.GRID)}
          >
            <i className="umo-toolbar__sort-by-grid" />
          </Button>
          <Button
            active={umoGalleryViewType === viewTypes.GRID_NOJ}
            onClick={() => changeViewType(viewTypes.GRID_NOJ)}
          >
            <i className="umo-toolbar__sort-by-uniform" />
          </Button>
          <Button
            active={umoGalleryViewType === viewTypes.SM_GRID_NOJ}
            onClick={() => changeViewType(viewTypes.SM_GRID_NOJ)}
          >
            <div>
              <i className="umo-toolbar__sort-by-small-grid" />
              <i className="umo-toolbar__sort-by-small-grid" />
            </div>
          </Button>
          <Button
            active={umoGalleryViewType === viewTypes.LIST}
            onClick={() => changeViewType(viewTypes.LIST)}
          >
            <i className="umo-toolbar__sort-by-list" />
          </Button>
        </ButtonGroup>
      </div>
      <div className="umo-toolbar__sort">
        <DropdownButton
          onSelect={(eventKey, e) => sortByUpdate(eventKey, e.target.dataset.isdesc === 'true')}
          title={intl.formatMessage({ id: 'searchResults.sortBy' })}
          size="sm"
          variant="default"
          className="umo-toolbar__sort-button"
          id="umo-sort-dropdown"
          alignRight
          disabled={!umoList || umoList.size === 0}
        >
          <Dropdown.Item
            className="umo-toolbar__sort-item"
            // the negation for isDescending allows it to work when sortByIsDescending is either
            // false or undefined
            active={sortBy === 'NONE'}
            eventKey={'NONE'}
            key={sortByItems.length}
            data-isdesc={false}
          >
            {intl.formatMessage({ id: 'searchResults.sortByNone' })}
          </Dropdown.Item>
          {sortByItems}
        </DropdownButton>
      </div>
      {session.get('publicUsr') ? null :
        <div className="umo-toolbar__options">
          <OptionsMenu
            dispatch={dispatch}
            searchParams={searchParams}
            selectedWorkspaceData={selectedWorkspaceData}
            selectedUMOIDs={selectedUMOIDs}
            isSelectAll={isSelectAll}
            isUMOGalleryWorkspace={isUMOGalleryWorkspace}
            title={intl.formatMessage({ id: 'searchResults.options' })}
            size="sm"
            id="umo-options-dropdown"
            umoList={umoList}
            disabled={!umoList || umoList.size === 0}
          />
        </div>
      }
      { appConfig.enableCopyUMOs && !isUMOGalleryWorkspace &&
        (isSelectAll || selectedUMOIDs.size > 0) ?
          <div className="umo-toolbar__options">
            <Button variant="outline-info" size="sm" draggable onClick={copyUMOs}>
              <div>
                <i className="umo-toolbar__copy-icon" />&nbsp;
                {intl.formatMessage({ id: 'searchResults.copy' })}
              </div>
            </Button>
          </div>
        : null
      }
      {
        appConfig.enableUmoCompare && selectedUMOIDs.size > 1 && selectedUMOIDs.size < 4 ?
          <div className="umo-toolbar__options">
            <Button variant="outline-info" size="sm" onClick={toggleCompareView}>
              <div>
                <i className="fas fa-clone" />&nbsp;
                {intl.formatMessage({ id: 'searchResults.compare' })}
              </div>
            </Button>
          </div>
          : null
      }
    </div>
  );
}

UMOToolbar.propTypes = {
  dispatch: PropTypes.func,
  sortByUpdate: PropTypes.func,
  sortBy: PropTypes.string,
  sortByIsDescending: PropTypes.bool,
  umoGalleryViewType: PropTypes.string,
  searchParams: PropTypes.instanceOf(SearchParams),
  selectedWorkspaceData: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  selectedUMOIDs: PropTypes.instanceOf(List),
  isSelectAll: PropTypes.bool,
  isUMOGalleryWorkspace: PropTypes.bool,
  copyUMOs: PropTypes.func,
  umoList: PropTypes.instanceOf(List),
  toggleCompareView: PropTypes.func,
};

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(UMOToolbar));



// WEBPACK FOOTER //
// ./src/js/components/UMOGallery/UMOToolbar.jsx